import { getApiErrorStatus } from '@/lib/api-error-utils';

/**
 * The substring every captcha refusal the API puts on the wire contains.
 *
 * Deliberately identical to the api's own classifier, `RateLimitAspect.isRecaptchaFailure`
 * (`api/src/main/java/com/valetmanager/api/ratelimit/RateLimitAspect.java:121`), which does
 * `message.toLowerCase().contains("captcha validation")`. Keep the two in step: the backend
 * needs the same answer we do (a captcha refusal must not count toward the failed-response
 * rate limit), so a divergence means one of the two is wrong about the same response.
 *
 * An exhaustive scan of the api source finds exactly three messages that reach a client, and
 * all three contain this substring:
 *   - "Captcha Validation Failed"                 (45 branches)
 *   - "Error occurs during Captcha Validation"    (38 branches)
 *   - "Error occurred during Captcha Validation"  (6 branches)
 * The "occurs"/"occurred" split is a typo that was copied around, not two distinct conditions.
 */
const CAPTCHA_MESSAGE = 'captcha validation';

/** The `output` code the API sets on a body it is refusing for captcha reasons. */
const CAPTCHA_OUTPUT_CODE = 'C';

/** The `output` code the API sets on a request-pickup body it accepted and queued. */
const ACCEPTED_OUTPUT_CODE = 'A';

/** The `output` code the API sets when the garage requires payment before retrieval (PB-7891). */
const PAYMENT_REQUIRED_OUTPUT_CODE = 'P';

function readMessage(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const message = (value as { message?: unknown }).message;
  return typeof message === 'string' ? message : undefined;
}

/**
 * Whether an Axios error is the API refusing the reCAPTCHA token (shape A).
 *
 * ## Invariants:
 * - Matches the substring `"captcha validation"`, never one of the three full messages. Why:
 *   there is no error code to key on — `ApplicationExceptionHandler:90` constructs
 *   `new RestError(null, message, null, null, null)`, so `code` is explicitly null and
 *   `@JsonInclude(NON_EMPTY)` drops it from the JSON entirely. The message text is the only
 *   discriminator that exists, and an exact match would silently stop firing the moment a
 *   caller hit one of the other two wordings.
 * - Narrower than a bare `/captcha/i`. Why: the api's own classifier requires both words, and
 *   matching the single word "captcha" would also match unrelated messages that merely mention
 *   it — including the OpenAPI descriptions that reach clients through error envelopes.
 * - Requires 400 as well as the text. Why: `RecaptchaValidationException` extends
 *   `ReservationNotFoundException`, which the shared handler maps to 400 BAD_REQUEST. Any other
 *   status carrying those words is something else, and replaying it just wastes the customer's
 *   time twice.
 *
 * ## Known gap — not a bug, do not "fix" by loosening the match:
 * `PermitRateCardController:619` (apply permit coupon) accepts EITHER a JWT or a captcha token
 * via `validateRequestEither`, and on failure of either throws
 * `ReservationNotFoundException("Authorization Failed. Please try again")` — a message with no
 * captcha wording at all. A captcha refusal there is indistinguishable on the wire from a bad
 * session token, so this returns false for it, and must: putting a puzzle in front of someone
 * whose JWT expired would be the wrong remedy and would never succeed. Closing that gap needs
 * an api change (split the two causes so the captcha branch throws
 * `RecaptchaValidationException` like the other 71 branches do).
 */
export function isCaptchaRefusedError(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 400) return false;
  if (!error || typeof error !== 'object' || !('response' in error)) return false;
  const response = (error as { response?: { data?: unknown } }).response;
  const message = readMessage(response?.data);
  return !!message && message.toLowerCase().includes(CAPTCHA_MESSAGE);
}

/**
 * Whether a **successful** (2xx) response body is actually the API refusing the token (shape B).
 *
 * Two endpoints do not throw on a captcha refusal — they stamp `output: "C"` onto the request
 * object and hand it straight back with HTTP 200:
 *   - touchless payment   `TicketServiceImpl:1767, :1771`
 *   - request pickup      `TicketServiceImpl:2084, :2090`
 *
 * ## Invariants:
 * - `output === "C"` is treated as captcha-exclusive. Why: `setOutput("C")` appears exactly four
 *   times in the api and every one is a captcha branch. The field also carries "A" (accepted)
 *   and, on request pickup only, "P" (payment required before retrieval, PB-7891) — neither of
 *   which is a captcha refusal, so this must stay false for both. Request pickup classifies all
 *   of its values through `classifyRequestTicketOutput` below. Verified against the api source,
 *   not inferred from the field name.
 * - Checked on the 2xx path, not the error path. Why: this refusal never becomes an exception,
 *   so a captcha handler wired only to rejected promises is silent on precisely the two
 *   endpoints where the customer is being told a payment succeeded that did not.
 */
export function isCaptchaRefusedBody(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  return (data as { output?: unknown }).output === CAPTCHA_OUTPUT_CODE;
}

/** How the API answered a request-pickup (`requestTicket`) call that came back 2xx. */
export type RequestTicketOutcome = 'accepted' | 'captchaRefused' | 'paymentRequired' | 'failed';

/**
 * Classifies a **successful** (2xx) `requestTicket` response body by its `output` code.
 *
 * `TicketServiceImpl.requestTicket` answers every non-throwing path with HTTP 200 and stamps the
 * outcome onto `output`: "A" when the pickup was queued, "C" when the captcha was refused, and
 * "P" when the garage requires the balance to be paid before the vehicle is retrieved (PB-7891).
 *
 * ## Invariants:
 * - Only "A" is `accepted`. Why: a 200 is not a success on this endpoint — "C" and "P" are both
 *   refusals delivered as 200, and treating "anything that is not C" as success is exactly how
 *   "P" first shipped telling the guest their car was on its way when nothing was queued. A value
 *   the api adds later (or a missing `output`) is therefore `failed`, never `accepted`; every
 *   api return path sets `output`, so a missing one is not a real success shape either.
 * - "C" is decided by `isCaptchaRefusedBody`, so the captcha path stays byte-identical to the
 *   payment endpoint's and the shared captcha invariant has one owner.
 * - "P" is not a failure to retry blindly: nothing was written server-side, and the remedy is to
 *   pay, then ask again.
 */
export function classifyRequestTicketOutput(data: unknown): RequestTicketOutcome {
  if (isCaptchaRefusedBody(data)) return 'captchaRefused';
  if (!data || typeof data !== 'object') return 'failed';
  const output = (data as { output?: unknown }).output;
  if (output === ACCEPTED_OUTPUT_CODE) return 'accepted';
  if (output === PAYMENT_REQUIRED_OUTPUT_CODE) return 'paymentRequired';
  return 'failed';
}
